// OPTIONS · Scanner (read-only). ONE table, ONE mode (2026-10-09): the Entry Price Mode saved in Scanner
// Settings (LTP or OFFER) decides ranking, qualification, the opportunity shown as current, the opportunity
// count, the Trade Ticket's default and the order's entry price. Only pairs whose prices are USABLE in that
// mode are listed (the worker applies the Trade Ticket's own freshness / book rules), so the Scanner and the
// ticket never disagree; stale rows live in Validation (diagnostics). "Show analysis columns" only ADDS
// diagnostic columns - it never changes the mode or the rows.
import React, { useCallback, useMemo, useState } from "react";
import { Info, Search, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import optionsApi, { optionsError } from "@/lib/optionsApi";
import { defaultSort, nextSort, RANKINGS, sortRows } from "@/lib/optionsSort";
import { FairValue, pct, rupees, timeLeft } from "@/components/options/PairValuation";
import RowStateBadge from "@/components/RowStateBadge";
import { useEntryWindows, withEntryWindow } from "@/lib/entryWindow";
import { age, MarketContext, PauseButton, px, ScannerStatusBar, StatusPill, usePoll } from "@/components/options/OptionsCommon";
import DepthDrawer from "@/components/options/DepthDrawer";
import SellScanner from "@/components/options/SellScanner";
import { useAdvanced } from "@/lib/advanced";
import { visibleColumns } from "@/lib/columnPrefs";
import { marginCell } from "@/lib/optionsText";
import ResetDefaultsButton from "@/components/ResetDefaultsButton";
import { ColumnsMenu, CONTROL, FilterBar, FilterField, ScannerSettingsButton, ScannerTable, useColumnPrefs } from "@/components/options/ScannerTable";

// 2026-10-27 -> 27-10-2026 (display only)
const dmy = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso || "") ? iso.split("-").reverse().join("-") : iso || "—");
const sgn = (v, f) => (v === null || v === undefined ? "—" : <span className={v >= 0 ? "text-[#00C805]" : "text-[#FF3B30]"}>{f(v)}</span>);
const n0 = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("en-IN"));
const sec = (v) => (v === null || v === undefined ? "—" : age(v));
const spreadPct = (l) => (l?.ask > 0 && l?.bid > 0 ? `${(((l.ask - l.bid) / l.ask) * 100).toFixed(2)}%` : "—");
const ivPct = (v) => (v === null || v === undefined ? "—" : `${(Number(v) * (Number(v) < 3 ? 100 : 1)).toFixed(1)}%`);

/** A row is TRADEABLE NOW in `mode` when its prices are usable in that mode: it qualifies (opportunity
 *  >= 0) or is NEAR (usable prices, opportunity just below 0 - the ticket's Spread to Maintain decides). */
export const usableIn = (r, mode) => !!(r?.[mode]?.qualified || r?.[mode]?.near);

// Columns (2026-10-14): the first three (Trade, Underlying, Und. Price) are PROTECTED - always shown, in place.
// Every other column is optional (Columns menu: show / hide / reorder, per user, BUY separately from SELL).
// Hidden by default: Time Left and the fair-value columns - their numbers still arrive and stay in the drawer.
const TD = "px-1.5 py-1 text-right whitespace-nowrap mono";
const tag = "ml-1 text-[9px] font-bold px-1 rounded-sm";
const MODE_COLS = { LTP: ["ltp_combined", "ltp_opp"], OFFER: ["offer_combined", "offer_opp"] };
const hiTd = (k) => (r, c) => `${TD} ${MODE_COLS[c.mode].includes(k) ? "bg-[#2563EB]/10 font-bold" : ""}`;
const val = (r) => r.valuation || {};
const lotOrTotal = (V, k) => (V.lots ? V[`${k}_profit_total`] : V[`${k}_profit_lot`]);
const perLot = (V, k) => `per lot ${rupees(V[`${k}_profit_lot`])}${V.lots ? ` · ${V.lots} lots ${rupees(V[`${k}_profit_total`])}` : ""}`;
const staleLtp = (r) => ((r.LTP || {}).warnings || []).some((w) => String(w).endsWith("LTP_OLD_TRADE"));
const SETTLEMENT_TAG = {
  PHYSICAL: ["PHYSICAL", "Stock option: an ITM leg held to expiry is a delivery of shares - delivery STT and margin are not in the net"],
  DEVOLVES_TO_FUTURES: ["DEVOLVES", "MCX option: an ITM leg at expiry becomes a futures position - not in the net"],
};
const NET_STATUS = { PENDING: "contract multiplier not measured yet", CHARGES_NOT_MODELLED: "BSE option charges are not modelled" };

/** Net ₹ per lot (minimum, after entry charges and exercise STT) with INDICATIVE / settlement tags. */
function NetCell({ r }) {
  const a = r.assessment;
  if (!a) return "—";
  const n = a.net || {};
  const st = SETTLEMENT_TAG[a.settlement];
  const tip = n.status === "OK"
    ? `Minimum gross ${rupees(n.gross_lot_min)} − entry charges ${rupees(n.entry_charges_lot)} − exercise STT ${rupees(n.exercise_stt_lot)} (underlying ends between the strikes)`
    : (NET_STATUS[n.status] || n.status);
  return (<span data-testid={`options-net-${r.pair_id}`} title={tip}>
    {n.status === "OK" ? sgn(n.net_lot_min, rupees) : "—"}
    {a.basis === "INDICATIVE" ? <span className={`${tag} bg-[#B45309]/15 text-[#B45309]`} data-testid={`options-indicative-${r.pair_id}`}
      title="Priced on last traded prices (or a leg has warnings): not where an order can execute now">INDICATIVE</span> : null}
    {st ? <span className={`${tag} bg-[var(--term-s3)] text-[var(--term-muted)]`} data-testid={`options-settlement-${r.pair_id}`}
      title={st[1]}>{st[0]}</span> : null}
  </span>);
}

const nearTag = (r) => <span className={`${tag} bg-[var(--term-s3)] text-[var(--term-muted)]`} data-testid={`options-near-${r.pair_id}`}
  title="NEAR: prices usable but the opportunity is below 0 - the ticket's Spread to Maintain decides">NEAR</span>;
const col = (id, label, cell, extra = {}) => ({ id, label, cell, sort: id, thId: `options-tcol-${id}`, td: TD,
  th: (c) => (MODE_COLS[c.mode].includes(id) ? "bg-[#2563EB]/15" : ""), ...extra });

export const BUY_PROTECTED = [
  { id: "trade", label: "Trade", align: "center", td: "px-1.5 py-1 text-center", cell: (r, c) => {
    const M = r[c.mode] || {};
    const tradeable = usableIn(r, c.mode);
    return (
      <button type="button" data-testid={`options-trade-${r.pair_id}`} disabled={!tradeable}
        title={tradeable ? `Open the trade ticket at ${c.mode === "LTP" ? "these LTPs" : "these offers"} (nothing is created or sent until you confirm)`
          : `Not tradeable now in ${c.mode} mode: ${M.reason || "prices not usable"}`}
        className="px-3 py-0.5 rounded-sm text-[11px] font-bold bg-[#2563EB] text-white hover:bg-[#1d4fd8] disabled:opacity-30"
        onClick={(e) => { e.stopPropagation(); if (tradeable) c.onOpen(r.pair_id, "hedge"); }}>TRADE</button>);
  } },
  col("underlying", "Underlying", (r) => r.underlying, { align: "left", td: "px-1.5 py-1 text-left font-bold whitespace-nowrap" }),
  col("underlying_ltp", "Und. Price", (r) => px(r.underlying_ltp)),
];

export const BUY_OPTIONAL = [
  col("state", "State", (r) => <RowStateBadge state={r.state} testId={`options-state-${r.pair_id}`} />, { align: "left" }),
  col("expiry", "Expiry", (r) => dmy(r.expiry)),
  col("time_left", "Time Left", (r) => <span data-testid={`options-time-left-${r.pair_id}`}>{timeLeft(val(r).seconds_to_expiry)}</span>, { defaultHidden: true }),
  col("ce_strike", "CE Strike", (r) => <span title={r.ce.tradingsymbol}>{r.ce.strike}</span>, { td: `${TD} font-semibold` }),
  col("ce_ltp", "CE LTP", (r) => px(r.ce.ltp), { td: (r, c) => `${TD} ${c.mode === "LTP" ? "bg-[#2563EB]/10" : ""}` }),
  col("ce_ask", "CE Offer", (r) => px(r.ce.ask), { td: (r, c) => `${TD} text-[#FF3B30] ${c.mode === "OFFER" ? "bg-[#2563EB]/10" : ""}` }),
  col("ce_fv", "CE Fair", (r) => <FairValue leg={val(r).ce_val || {}} testId={`options-ce-fv-${r.pair_id}`} />, { defaultHidden: true }),
  col("pe_strike", "PE Strike", (r) => <span title={r.pe.tradingsymbol}>{r.pe.strike}</span>, { td: `${TD} font-semibold` }),
  col("pe_ltp", "PE LTP", (r) => px(r.pe.ltp), { td: (r, c) => `${TD} ${c.mode === "LTP" ? "bg-[#2563EB]/10" : ""}` }),
  col("pe_ask", "PE Offer", (r) => px(r.pe.ask), { td: (r, c) => `${TD} text-[#FF3B30] ${c.mode === "OFFER" ? "bg-[#2563EB]/10" : ""}` }),
  col("pe_fv", "PE Fair", (r) => <FairValue leg={val(r).pe_val || {}} testId={`options-pe-fv-${r.pair_id}`} />, { defaultHidden: true }),
  col("strike_diff", "Strike Spread", (r) => px(r.strike_diff)),
  col("ltp_combined", "LTP Sum", (r) => px((r.LTP || {}).combined), { td: hiTd("ltp_combined") }),
  col("offer_combined", "Offer Sum", (r) => px((r.OFFER || {}).combined), { td: hiTd("offer_combined") }),
  col("fv_sum", "Fair Sum", (r) => px(val(r).fair_value_sum), { defaultHidden: true }),
  col("ltp_opp", "LTP Opp", (r, c) => {
    const L = r.LTP || {}, O = r.OFFER || {}, M = r[c.mode] || {};
    return (<span data-testid={`options-ltp-opp-${r.pair_id}`}>{sgn(L.opp, px)}
      {staleLtp(r) && L.opp > 0 ? <span className={`${tag} bg-[#B45309]/15 text-[#B45309]`} data-testid={`options-stale-ltp-${r.pair_id}`}
        title="At least one leg's LTP is an OLD trade: this opportunity is not current. Executable now is the Offer Opp.">STALE LTP</span> : null}
      {L.opp > 0 && O.opp != null && O.opp < 0 ? <span className={`${tag} bg-[var(--term-s3)] text-[var(--term-muted)]`} data-testid={`options-not-executable-${r.pair_id}`}
        title={`At the offers (what buying now costs) the opportunity is ${O.opp}`}>not at offers</span> : null}
      {c.mode === "LTP" && !M.qualified && M.near ? nearTag(r) : null}</span>);
  }, { td: (r, c) => `${hiTd("ltp_opp")(r, c)} ${staleLtp(r) ? "opacity-60" : ""}` }),
  col("offer_opp", "Offer Opp", (r, c) => {
    const O = r.OFFER || {}, M = r[c.mode] || {};
    return (<span data-testid={`options-offer-opp-${r.pair_id}`}>{sgn(O.opp, px)}
      {c.mode === "OFFER" && !M.qualified && M.near ? nearTag(r) : null}</span>);
  }, { td: hiTd("offer_opp") }),
  // Scanner audit B4: the opportunity per lot after entry charges and exercise STT (the minimum: underlying between
  // the strikes), and whether the row is priced where an order can execute / how it settles. API-computed.
  col("net_lot", "Net ₹/lot", (r) => <NetCell r={r} />),
  col("fv_opp", "Fair Opp", (r) => sgn(val(r).fair_value_opportunity, px), { defaultHidden: true }),
  col("expiry_lot", "Expiry ₹", (r) => <span title={`${perLot(val(r), "expiry")} (underlying ends at the current price)`}>{sgn(lotOrTotal(val(r), "expiry"), rupees)}</span>),
  col("expiry_pct", "Expiry %", (r) => pct(val(r).expiry_profit_pct)),
  // 1-Day (backend option_valuation, 14 Oct 2026): EXECUTABLE = tomorrow's exit at the bid − entry in the scanner's mode;
  // DECAY = time value lost overnight at each leg's own IV, price unchanged; ACTUAL = the LTPs since the previous close
  col("one_day_lot", "1-Day exec ₹", (r) => <span title={`${perLot(val(r), "one_day")} · underlying unchanged, one trading day later, exit at the bid, entry at the offers`}>{sgn(lotOrTotal(val(r), "one_day"), rupees)}</span>),
  col("one_day_pct", "1-Day exec %", (r) => <span data-testid={`options-1d-pct-${r.pair_id}`} title="1-Day executable P&L ÷ entry premium">{pct(val(r).one_day_profit_pct)}</span>),
  col("one_day_decay_lot", "1-Day decay ₹", (r) => <span data-testid={`options-1d-decay-${r.pair_id}`} title={`${perLot(val(r), "one_day_decay")} · time value lost by the next trading day at each leg's own IV, underlying unchanged ("—": a leg's IV could not be implied)`}>{sgn(lotOrTotal(val(r), "one_day_decay"), rupees)}</span>),
  col("one_day_decay_pct", "1-Day decay %", (r) => pct(val(r).one_day_decay_profit_pct), { defaultHidden: true }),
  col("actual_1d_lot", "1D actual ₹", (r) => <span data-testid={`options-1d-actual-${r.pair_id}`} title={`${perLot(val(r), "actual_1d")} · history: both legs' LTP − previous close (not a projection)`}>{sgn(lotOrTotal(val(r), "actual_1d"), rupees)}</span>),
  col("funds_lot", "Funds / lot", (r) => <span title="Premium for one lot at the offers">{rupees(val(r).funds_per_lot)}</span>),
  col("margin", "Margin", (r) => <MarginCell capital={r.capital} />),
  col("lot_size", "Lot Size", (r) => r.lot_size ?? "—"),
  col("lots", "Lots", (r) => <span data-testid={`options-lots-${r.pair_id}`}>{val(r).lots ?? "—"}</span>),
  col("roi", "ROI %", (r) => <span title="Expiry Profit at the current price / funds used">{pct(val(r).potential_roi_pct)}</span>),
];

// Diagnostics added by "Show analysis columns" (nothing else changes); always after the chosen columns
const dcol = (label, cell, td = TD) => ({ id: `diag_${label}`, label, cell, td });
const DIAG = [
  dcol("CE Bid × Qty", (r) => `${px(r.ce.bid)} × ${n0(r.ce.bid_qty)}`), dcol("CE Offer × Qty", (r) => `${px(r.ce.ask)} × ${n0(r.ce.ask_qty)}`),
  dcol("PE Bid × Qty", (r) => `${px(r.pe.bid)} × ${n0(r.pe.bid_qty)}`), dcol("PE Offer × Qty", (r) => `${px(r.pe.ask)} × ${n0(r.pe.ask_qty)}`),
  dcol("CE LTP age", (r) => sec(r.ce.trade_age_s)), dcol("PE LTP age", (r) => sec(r.pe.trade_age_s)),
  dcol("CE tick age", (r) => sec(r.ce.tick_age_s)), dcol("PE tick age", (r) => sec(r.pe.tick_age_s)),
  dcol("CE OI", (r) => n0(r.ce.oi)), dcol("PE OI", (r) => n0(r.pe.oi)), dcol("CE Vol", (r) => n0(r.ce.volume)), dcol("PE Vol", (r) => n0(r.pe.volume)),
  dcol("CE IV", (r) => ivPct((val(r).ce_val || {}).iv)), dcol("PE IV", (r) => ivPct((val(r).pe_val || {}).iv)),
  dcol("CE spread", (r) => spreadPct(r.ce)), dcol("PE spread", (r) => spreadPct(r.pe)),
  dcol("Freshness", (r, c) => <span data-testid={`options-fresh-${r.pair_id}`}>CE {c.mode === "LTP" ? r.ce.ltp_status : r.ce.offer_status} · PE {c.mode === "LTP" ? r.pe.ltp_status : r.pe.offer_status}</span>, `${TD} text-[10px]`),
  dcol("Reasons", (r, c) => { const M = r[c.mode] || {}; return [M.reason, ...(M.warnings || [])].filter(Boolean).join(", ") || "—"; }, `${TD} text-[10px] text-left`),
];
const DIAG_HEAD = DIAG.map((c) => <th key={c.id} className="px-1.5 py-2 whitespace-nowrap font-semibold text-right text-[var(--term-muted)]" data-testid="options-diag-col">{c.label}</th>);
/** Broker margin (Zerodha calculator, top rows only) - or why there is none; never an invented figure. */
export function MarginCell({ capital }) {
  const m = marginCell(capital);
  if (m.value != null) return <span title={`Zerodha margin calculator, one lot of each leg${m.stale ? " (older than a minute)" : ""}`} className={m.stale ? "opacity-60" : ""}>{rupees(m.value)}</span>;
  return <span className="text-[10px] text-[var(--term-muted)]" title={m.title} data-testid="options-margin-reason">{m.text}</span>;
}
const buyRowProps = (onOpen) => (r) => ({ className: "gz-row cursor-pointer hover:bg-[var(--term-hover)]", onClick: () => onOpen(r.pair_id),
  "data-testid": `options-row-${r.pair_id}`, title: `${r.ce.tradingsymbol} / ${r.pe.tradingsymbol}` });

// Scanner filters shown above the table: the scanner's own Settings values, saved through the same
// /settings endpoint (applied live by the worker; scanner maths unchanged).
function MinOiControl({ value, onSaved }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const shown = v !== "" ? v : (value ?? "");
  const save = async () => {
    if (v === "" || Number(v) === value) { setV(""); return; }
    const n = Number(v);
    if (!Number.isInteger(n) || n < 0) { toast.error("Minimum OI must be a whole number ≥ 0"); return; }
    setBusy(true);
    try { await optionsApi.put("/settings", { min_oi: n }); toast.success(`Minimum OI (both legs) ${n}`); setV(""); onSaved(); }
    catch (e) { toast.error(optionsError(e)); } finally { setBusy(false); }
  };
  return (
    <FilterField label="Min OI (both legs)" title="Both legs must have at least this OI (saved scanner setting)">
      <input value={shown} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && save()}
        disabled={busy} className={`${CONTROL} w-24 mono text-right`} data-testid="options-min-oi" />
    </FilterField>
  );
}

/** Allot Funds: the scanner computes Number of Lots / totals / ROI per pair from it (worker setting). */
function AllotFundsControl({ value, onSaved }) {
  const [v, setV] = useState("");
  const shown = v !== "" ? v : (value ?? "");
  const save = async () => {
    if (v === "") return;
    const n = v.trim() === "0" ? null : Number(v);
    if (n !== null && (!Number.isFinite(n) || n <= 0)) { toast.error("Allot Funds must be a positive amount (0 clears it)"); return; }
    if (n === value) { setV(""); return; }
    try { await optionsApi.put("/settings", { allot_funds: n }); toast.success(n ? `Allot Funds ₹${n}` : "Allot Funds cleared"); setV(""); onSaved(); }
    catch (e) { toast.error(optionsError(e)); }
  };
  return (
    <FilterField label="Allot Funds ₹" title="Funds for one trade: Lots = floor(Allot Funds / funds per lot). 0 clears it. (saved scanner setting)">
      <input value={shown} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && save()}
        placeholder="—" className={`${CONTROL} w-28 mono text-right`} data-testid="options-allot-funds" />
    </FilterField>
  );
}

const ANALYSIS_KEY = "gz.options.scanner.analysisColumns";
const readFlag = (k) => { try { return window.localStorage.getItem(k) === "1"; } catch (e) { return false; } };
const writeFlag = (k, v) => { try { window.localStorage.setItem(k, v ? "1" : "0"); } catch (e) { /* ignore */ } };

/** BUY | SELL OPTIONS: which table the worker builds (saved in Scanner settings, like the entry price). */
function DirectionToggle({ value, onSaved }) {
  const [busy, setBusy] = useState(false);
  const set = async (dir) => {
    if (dir === value || busy) return;
    setBusy(true);
    try { await optionsApi.put("/settings", { scan_direction: dir }); toast.success(dir === "SELL" ? "Scanner: SELL OPTIONS" : "Scanner: BUY OPTIONS"); onSaved(); }
    catch (e) { toast.error(optionsError(e)); } finally { setBusy(false); }
  };
  const btn = (dir, on) => `px-3 py-1 text-[11px] font-bold ${on ? (dir === "SELL" ? "bg-[#B45309] text-white" : "bg-[#2563EB] text-white") : "text-[var(--term-muted)]"}`;
  return (
    <div className="flex items-center rounded-sm border border-[var(--term-border-strong)] overflow-hidden" data-testid="options-direction"
      title="BUY OPTIONS: long ITM CE + long ITM PE (unchanged). SELL OPTIONS: short CE + short PE, any strikes.">
      <button type="button" disabled={busy} className={btn("BUY", value !== "SELL")} onClick={() => set("BUY")} data-testid="options-direction-buy">BUY</button>
      <button type="button" disabled={busy} className={btn("SELL", value === "SELL")} onClick={() => set("SELL")} data-testid="options-direction-sell">SELL</button>
      <span className="px-2 text-[9px] text-[var(--term-muted)]" data-testid="options-direction-note"
        title="Switching is not just a view: it changes the saved scanner setting (scan direction) and the scanner rebuilds its table for every screen that uses it">saved setting</span>
    </div>
  );
}

function secondsAgo(iso) {
  if (!iso) return null;
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  return s < 0 ? 0 : s;
}

/** One line: live or not, which market, how many opportunities, how fresh. Details on request. */
function ScannerSummary({ status, data, count, onChanged, showDetails, setShowDetails, advanced }) {
  const rt = status?.runtime || {};
  const running = rt.status === "RUNNING";
  const live = running && (rt.feed_state === "LIVE" || rt.feed_state === "REPLAY") && rt.feed?.connected !== false;
  const market = data?.market || status?.settings?.market;
  const unds = data?.underlyings || rt.scanner?.underlyings || [];
  const closed = unds.length > 0 && unds.every((u) => u.status === "MARKET_CLOSED");
  const ago = secondsAgo(data?.generated_at);
  const [busy, setBusy] = useState(false);
  const act = (what) => {
    setBusy(true);
    optionsApi.post(`/scanner/${what}`).then(() => { toast.success(what === "start" ? "Scanner started" : "Scanner stopped"); onChanged(); })
      .catch((e) => toast.error(optionsError(e))).finally(() => setBusy(false));
  };
  const failed = (rt.gate?.checks || []).filter((c) => !c.ok);
  return (
    <div className="space-y-1" data-testid="options-summary">
      <div className="flex items-center gap-3 flex-wrap text-xs">
        <span className={`h-2.5 w-2.5 rounded-full ${live ? "bg-[#00C805]" : running ? "bg-[#FFCC00]" : "bg-slate-400"}`} />
        <span className="font-bold" data-testid="options-summary-text">
          {!status ? "Connecting…" : !running ? "Scanner stopped" : !live ? "Waiting for live prices" : closed ? "Market closed" : "Live"}
          {market ? ` · ${market}` : ""}{running && live && !closed ? ` · ${count} opportunit${count === 1 ? "y" : "ies"}` : ""}
          {running && ago !== null ? ` · updated ${age(ago)} ago` : ""}
        </span>
        {status && (running
          ? <button disabled={busy} onClick={() => act("stop")} data-testid="options-stop" className="px-2 py-0.5 rounded-sm text-[11px] border border-[var(--term-border-strong)]">Stop</button>
          : <button disabled={busy} onClick={() => act("start")} data-testid="options-start" className="px-2 py-0.5 rounded-sm text-[11px] font-bold bg-[#2563EB] text-white">Start scanner</button>)}
        {advanced && <button className="underline text-[11px] text-[var(--term-muted)]" onClick={() => setShowDetails(!showDetails)} data-testid="options-feed-details-toggle">
          {showDetails ? "Hide feed details" : "Feed details"}</button>}
      </div>
      {closed && <div className="text-[11px] text-[var(--term-muted)]">Opportunities appear during the exchange session ({market === "MCX" ? "MCX trading hours" : "NSE 09:15–15:30 IST"}).</div>}
      {(rt.message || failed.length > 0) && !showDetails &&
        <div className="text-[11px] text-[#b38f00]" data-testid="options-summary-warning">Live prices are unavailable right now{rt.message ? ` — ${rt.message}` : ""}.</div>}
      {showDetails && <ScannerStatusBar status={status} onChanged={onChanged} />}
    </div>
  );
}

export default function OptionsScanner() {
  const [advanced] = useAdvanced();
  const [analysis, setAnalysisState] = useState(() => readFlag(ANALYSIS_KEY));
  const setAnalysis = (v) => { setAnalysisState(v); writeFlag(ANALYSIS_KEY, v); };
  const [showDetails, setShowDetails] = useState(false);
  const [info, setInfo] = useState(false);
  const [minOpp, setMinOpp] = useState("");
  const [paused, setPaused] = useState(false);
  const [sort, setSort] = useState(null);
  const [q, setQ] = useState("");
  const [underlying, setUnderlying] = useState("");
  const [expiry, setExpiry] = useState("");
  const [open, setOpen] = useState(null);
  const [focus, setFocus] = useState(null);
  const openRow = useCallback((id, f = null) => { setOpen(id); setFocus(f); }, []);
  const status = usePoll("/status", { ms: 3000 });
  // THE one source of truth: the Entry Price Mode saved in Scanner Settings (never switched here)
  const mode = status.data?.settings?.entry_price_mode === "OFFER" ? "OFFER" : "LTP";
  const direction = status.data?.settings?.scan_direction === "SELL" ? "SELL" : "BUY";
  // nothing is asked for (or drawn) in a default mode before the saved settings arrive: no LTP flash for an OFFER user
  const opp = usePoll("/opportunities", { ms: 2000, paused: paused || direction === "SELL" || !status.data, params: { mode, limit: 500, direction: "BUY" } });
  const [sellData, setSellData] = useState(null);
  const d = direction === "SELL" ? sellData : opp.data;
  const reload = () => { status.reload(); opp.reload(); };
  // the worker's rows for `mode`, kept only when usable in that mode (never a stale or other-mode row)
  const usable = useMemo(() => (d?.mode && d.mode !== mode ? [] : (d?.rows || []).filter((x) => usableIn(x, mode))), [d, mode]);
  const windows = useEntryWindows(direction === "SELL" ? [] : usable);       // W5: the trader's own entry rule
  const current = useMemo(() => usable.map((x) => withEntryWindow(x, windows)), [usable, windows]);

  const rows = useMemo(() => {
    let r = current;
    if (underlying) r = r.filter((x) => x.underlying === underlying);
    if (expiry) r = r.filter((x) => x.expiry === expiry);
    if (q.trim()) {
      const s = q.trim().toUpperCase();
      r = r.filter((x) => `${x.underlying} ${x.ce.tradingsymbol} ${x.pe.tradingsymbol}`.toUpperCase().includes(s));
    }
    if (minOpp !== "" && !Number.isNaN(Number(minOpp))) r = r.filter((x) => (x[mode]?.opp ?? -Infinity) >= Number(minOpp));
    return sortRows(r, sort || defaultSort(mode));
  }, [current, underlying, expiry, q, sort, mode, minOpp]);

  const unds = d?.underlyings || status.data?.runtime?.scanner?.underlyings || [];
  const exps = [...new Set(unds.filter((u) => !underlying || u.underlying === underlying).map((u) => u.expiry).filter(Boolean))].sort();
  const buyCols = useColumnPrefs("BUY", BUY_OPTIONAL);
  const buyVisible = useMemo(() => visibleColumns(BUY_PROTECTED, BUY_OPTIONAL, buyCols.prefs), [buyCols.prefs]);
  const buyCtx = useMemo(() => ({ mode, onOpen: openRow }), [mode, openRow]);
  const buyRowPropsFn = useMemo(() => buyRowProps(openRow), [openRow]);
  const activeSort = sort || defaultSort(mode);
  // the opportunity count: pairs that QUALIFY now in the selected mode (opportunity >= 0, usable prices)
  const count = direction === "SELL" ? (sellData?.total ?? 0) : rows.filter((x) => x[mode]?.qualified).length;
  const label = mode === "LTP" ? "LTP" : "Offer";
  // the view filters go back to "everything, ranked by Opportunity"; Min OI is a saved scanner setting, so it
  // is set to the recommended value (that one save is why this asks first). Allot Funds is the trader's money: kept.
  const resetDefaults = async () => {
    setUnderlying(""); setExpiry(""); setQ(""); setMinOpp(""); setSort(null);
    try {
      const r = await optionsApi.get("/settings/defaults");
      if (r.data?.min_oi != null && r.data.min_oi !== status.data?.settings?.min_oi) await optionsApi.put("/settings", { min_oi: r.data.min_oi });
      toast.success("Recommended scanner filters restored");
      reload();
    } catch (e) { toast.error(optionsError(e)); }
  };

  if (!status.data) {
    return (
      <div className="p-4 space-y-3" data-testid="options-scanner-page">
        <h1 className="text-lg font-bold flex items-center gap-2"><SlidersHorizontal className="h-5 w-5" /> Options Scanner</h1>
        {status.error ? <div className="text-xs text-[#FF3B30]">{status.error}</div>
          : <div className="text-xs text-[var(--term-muted)]" data-testid="options-scanner-loading">Loading your scanner settings…</div>}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3" data-testid="options-scanner-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-lg font-bold flex items-center gap-2"><SlidersHorizontal className="h-5 w-5" /> Options Scanner</h1>
        {status.data && <DirectionToggle value={direction} onSaved={reload} />}
        {direction === "BUY" && <>
        <span className="text-xs text-[var(--term-muted)]" data-testid="options-heading">
          long lower-strike CE + long higher-strike PE pairs, ranked by {label} Opportunity (strike spread − {label} sum).
          Green = costs no more than the spread; NEAR rows cost a little more.</span>
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-sm bg-[#2563EB]/15 text-[#2563EB]" data-testid="options-entry-mode"
          title="Ranking, qualification, the Trade Ticket and the order all use this price. Change it in Scanner settings.">
          Entry price: {mode}</span>
        </>}
        <label className="ml-auto flex items-center gap-1 text-xs" title="Add diagnostic columns (quantities, ages, OI, volume, IV, spread, freshness, reasons). Nothing else changes.">
          <input type="checkbox" checked={analysis} onChange={(e) => setAnalysis(e.target.checked)} data-testid="options-analysis-toggle" /> Show analysis columns</label>
      </div>
      <ScannerSummary status={status.data} data={d} count={count} onChanged={reload} showDetails={showDetails}
        setShowDetails={setShowDetails} advanced={advanced || analysis} />
      {analysis && <MarketContext market={d?.market || status.data?.settings?.market} universeLabel={d?.universe_label}
        feedState={status.data?.runtime?.feed_state} />}
      {status.error && <div className="text-xs text-[#FF3B30]">{status.error}</div>}

      {direction === "SELL" && <SellScanner settings={status.data?.settings} onSaved={reload} onOpen={openRow} paused={paused} onData={setSellData} />}
      {direction === "BUY" && <>
      <FilterBar testId="options-buy-filters" actions={<>
        <ColumnsMenu protectedCols={BUY_PROTECTED} optional={BUY_OPTIONAL} {...buyCols} testId="options-columns" />
        <ResetDefaultsButton onReset={resetDefaults} testId="options-scanner-reset-defaults"
          confirm="Restore the recommended scanner filters? Underlying, expiry, search and Spread to Maintain are cleared, ranking goes back to Opportunity, and Min OI is saved at the recommended value. Allot Funds is kept."
          title="Clear the view filters and set Min OI to the recommended value (Allot Funds is kept)" />
        <ScannerSettingsButton />
      </>}>
        <FilterField label="Underlying">
          <select value={underlying} onChange={(e) => setUnderlying(e.target.value)} data-testid="options-underlying-filter" className={CONTROL}>
            <option value="">All underlyings</option>
            {[...new Set(unds.map((u) => u.underlying))].map((n) => <option key={n} value={n}>{n}</option>)}
          </select></FilterField>
        <FilterField label="Expiry">
          <select value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="options-expiry-filter" className={CONTROL}
            disabled={exps.length < 2}>
            <option value="">{exps.length === 1 ? dmy(exps[0]) : "All expiries"}</option>
            {exps.length > 1 && exps.map((x) => <option key={x} value={x}>{dmy(x)}</option>)}
          </select></FilterField>
        <FilterField label="Rank by" title={`Rank by (default: ${label} Opportunity - the selected entry price)`}>
          <select value={activeSort.key} onChange={(e) => setSort({ key: e.target.value, dir: "desc" })} data-testid="options-rank-by" className={CONTROL}>
            {RANKINGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            {!RANKINGS.some(([k]) => k === activeSort.key) && <option value={activeSort.key}>(column)</option>}
          </select></FilterField>
        <AllotFundsControl value={status.data?.settings?.allot_funds} onSaved={reload} />
        <FilterField label="Spread to Maintain" title={`Show only pairs whose ${label} Opportunity per unit is at or above this (display filter; a trade's own Spread to Maintain is set on its ticket)`}>
          <input value={minOpp} onChange={(e) => setMinOpp(e.target.value)} placeholder="any" data-testid="options-min-opp" className={`${CONTROL} w-24 mono text-right`} />
        </FilterField>
        <MinOiControl value={status.data?.settings?.min_oi} onSaved={reload} />
        <FilterField label="Search">
          <span className={`${CONTROL} flex items-center gap-1`}>
            <Search className="h-3 w-3" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Symbol" className="bg-transparent outline-none w-32" data-testid="options-search" />
          </span></FilterField>
        <FilterField label="Updates"><PauseButton paused={paused} setPaused={setPaused} /></FilterField>
        {sort && <button className="h-8 self-end underline text-[var(--term-muted)]" onClick={() => setSort(null)}>reset to {label} Opportunity ↓</button>}
      </FilterBar>
      {analysis && <div className="text-[var(--term-muted)] mono text-xs" data-testid="options-counts">
        {d?.running === false ? "scanner not running" :
          `${rows.length} shown · ${count} qualifying in ${mode} · NEAR ${d?.near_counts?.[mode] ?? 0} · snapshot v${d?.version ?? "—"} · ${d?.generated_at ? new Date(d.generated_at).toLocaleTimeString() : ""}`}
      </div>}

      {analysis && <div className="flex flex-wrap gap-1.5 text-[10px]">
        {unds.map((u) => (
          <span key={u.target || u.underlying} className="term-panel rounded-sm px-2 py-1 mono flex items-center gap-1" data-testid={`options-und-${u.target || u.underlying}`}>
            <b>{u.underlying}</b> {u.expiry} · {px(u.spot)} ({age(u.spot_age_s)}) <StatusPill status={u.status} />
            {u.counts && <span className="text-[var(--term-muted)]">ITM {u.counts.itm_calls}CE/{u.counts.itm_puts}PE · {u.counts.pairs} pairs · qualifying {mode} {u.counts.qualifying?.[mode]}
              {" "}· best <b className={u.counts.best_opp?.[mode] > 0 ? "text-[#00C805]" : ""}>{px(u.counts.best_opp?.[mode])}</b></span>}
            {u.reason && <span className="text-[var(--term-muted)]">{u.reason}</span>}
          </span>
        ))}
      </div>}

      <ScannerTable testId="options-scanner-table" cols={buyVisible} rows={rows} rowKey={(r) => r.pair_id} rowProps={buyRowPropsFn}
        ctx={buyCtx} sort={activeSort} onSort={(k) => setSort(nextSort(activeSort, k))}
        extraHead={analysis ? DIAG_HEAD : null} extraCells={analysis ? DIAG : null}
        maxHeight={analysis ? "calc(100vh - 360px)" : "calc(100vh - 280px)"}
        empty={<span data-testid="options-scanner-empty">{d?.running === false || (status.data && status.data.runtime?.status !== "RUNNING") ? "Start the scanner to see opportunities." :
          `No pair is tradeable at current ${mode === "LTP" ? "LTPs (both legs need a recent trade)" : "offers (both legs need a fresh, tight book with a full lot)"} right now. The list updates by itself.`}</span>} />
      <div className="text-[10px] text-[var(--term-muted)]">
        <button className="flex items-center gap-1 underline" onClick={() => setInfo(!info)}><Info className="h-3 w-3" /> How this table works</button>
        {info && <div className="mt-1">
          Every row is priced at the <b>{mode === "LTP" ? "last traded prices" : "best offers"}</b> (Entry price in Scanner settings) and
          ranked by {label} Opportunity, descending, across the selected underlyings and expiries. Only pairs whose prices are usable in
          this mode right now are listed - the same rules the Trade Ticket applies - so TRADE opens a ticket at the same prices and
          opportunity. Pairs with stale or missing prices are in Validation (diagnostics). NEAR rows have usable prices but cost a little
          more than the strike spread; the ticket&apos;s Spread to Maintain decides. Nothing is created or sent until you confirm.
        </div>}
      </div>
      </>}
      <DepthDrawer pairId={open} focus={focus} mode={mode} onClose={() => { setOpen(null); setFocus(null); }} />
    </div>
  );
}
